// src/pages/cmdb/PlanDeployTab.tsx
// Q-926 Sprint 6 — Tab "Plan de Deploy" en /cmdb.
// Caso de uso PRE-deploy: "qué scripts me faltan instalar en cliente X / BD Y / empresa Z".
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Badge, Spinner, EmptyState, toast,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Input, Label,
} from '@/components/ui';
import {
  ClipboardList, Eye, RefreshCw, AlertTriangle, FileCode2, Rocket,
} from 'lucide-react';
import { cmdbService } from '@/services/cmdbService';
import type {
  DeployPlanArtifact, DeployPlanResponse, ArtifactEstadoActual,
} from '@/services/cmdbService';
import { clientesService } from '@/services/clientes';
import type { Cliente, Empresa } from '@/types';
import { ViewScriptModal } from './ViewScriptModal';
import { ApplyArtifactDialog } from './ApplyArtifactDialog';

const SISTEMAS = ['qpweb', 'qgeo', 'qpos', 'qfield', 'erp_gateway', 'qcollab', 'qfleet'];

const ESTADO_BADGE: Record<ArtifactEstadoActual, { variant: 'default' | 'secondary' | 'outline' | 'destructive'; label: string }> = {
  match: { variant: 'default', label: 'Aplicado' },
  gap: { variant: 'destructive', label: 'Pendiente' },
  drift: { variant: 'destructive', label: 'Drift' },
  unknown: { variant: 'outline', label: 'Desconocido' },
  rogue: { variant: 'secondary', label: 'Rogue' },
  no_aplicado: { variant: 'outline', label: 'No aplicado' },
};

const SKIP_REASON_LABEL: Record<string, string> = {
  requiere_bd: 'Sin BD',
  feature_no_licenciada: 'Sin licencia',
  scope_no_aplica: 'Scope no aplica',
};

export function PlanDeployTab() {
  const [sistema, setSistema] = useState('qpweb');
  const [clienteId, setClienteId] = useState<string>('');
  const [bd, setBd] = useState('');
  const [empresaId, setEmpresaId] = useState<string>('');

  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const [plan, setPlan] = useState<DeployPlanResponse | null>(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [viewArtifact, setViewArtifact] = useState<DeployPlanArtifact | null>(null);
  // Q-930 Sprint 7: dialog "🚀 Aplicar" — emite apply_token + muestra curl
  const [applyArtifact, setApplyArtifact] = useState<DeployPlanArtifact | null>(null);

  // Load clientes catalog (review m1: cancelled flag para mount/unmount race)
  useEffect(() => {
    let cancelled = false;
    setLoadingFilters(true);
    clientesService.list({ activo: true, page_size: 200 })
      .then((r) => { if (!cancelled) setClientes(r.items); })
      .catch((e) => { if (!cancelled) toast.error(`Error cargando clientes: ${String(e)}`); })
      .finally(() => { if (!cancelled) setLoadingFilters(false); });
    return () => { cancelled = true; };
  }, []);

  // Load empresas when cliente changes
  useEffect(() => {
    let cancelled = false;
    setEmpresas([]);
    setEmpresaId('');
    if (!clienteId) return;
    clientesService.getEmpresas(clienteId)
      .then((r) => { if (!cancelled) setEmpresas(r); })
      .catch((e) => { if (!cancelled) toast.error(`Error cargando empresas: ${String(e)}`); });
    return () => { cancelled = true; };
  }, [clienteId]);

  const generarPlan = useCallback(async () => {
    if (!sistema || !clienteId) {
      toast.error('Seleccioná Sistema + Cliente para generar el plan');
      return;
    }
    setLoadingPlan(true);
    setPlan(null);
    try {
      const bdTrim = bd.trim();
      const empresaIdValue = empresaId && empresaId !== 'none' ? empresaId : undefined;
      const r = await cmdbService.getDeployPlan({
        sistema,
        cliente_id: clienteId,
        bd: bdTrim || undefined,
        empresa_id: empresaIdValue,
      });
      setPlan(r);
      toast.success(`Plan generado: ${r.totales.manifest} artifacts, ${r.totales.aplicables} aplicables`);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Error generando plan: ${msg}`);
    } finally {
      setLoadingPlan(false);
    }
  }, [sistema, clienteId, bd, empresaId]);

  const refrescarManifest = useCallback(async () => {
    if (!sistema) return;
    // review M3: cerrar modal abierto antes del refresh (puede invalidar contenido)
    setViewArtifact(null);
    setRefreshing(true);
    try {
      const r = await cmdbService.refreshManifest(sistema);
      toast.success(`Manifest refrescado: ${r.artifacts_count} artifacts cargados de GitHub`);
      // Re-generar plan si ya había uno
      if (plan) {
        await generarPlan();
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Error refrescando manifest: ${msg}`);
    } finally {
      setRefreshing(false);
    }
  }, [sistema, plan, generarPlan]);

  const isAged = useMemo(() =>
    plan?.snapshot_age_days != null && plan.snapshot_age_days > 7,
    [plan],
  );

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" /> Filtros del plan
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label htmlFor="sistema-select">Sistema</Label>
              <Select value={sistema} onValueChange={setSistema}>
                <SelectTrigger id="sistema-select" data-testid="plan-sistema-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SISTEMAS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cliente-select">Cliente</Label>
              {/* review M1: cliente REQUERIDO — value undefined deja placeholder visible sin warning Radix */}
              <Select
                value={clienteId || undefined}
                onValueChange={setClienteId}
                disabled={loadingFilters}
              >
                <SelectTrigger id="cliente-select" data-testid="plan-cliente-select">
                  <SelectValue placeholder="Elegí cliente..." />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="empresa-select">Empresa (opt)</Label>
              {/* review M1: Radix Select no acepta value="" — usar sentinel "none" */}
              <Select
                value={empresaId || 'none'}
                onValueChange={(v) => setEmpresaId(v === 'none' ? '' : v)}
                disabled={!clienteId || empresas.length === 0}
              >
                <SelectTrigger id="empresa-select" data-testid="plan-empresa-select">
                  <SelectValue placeholder="Cualquiera..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Cualquiera</SelectItem>
                  {empresas.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bd-input">BD (opt)</Label>
              <Input
                id="bd-input"
                value={bd}
                onChange={(e) => setBd(e.target.value)}
                placeholder="Avimtienda"
                data-testid="plan-bd-input"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={generarPlan}
                disabled={loadingPlan || !sistema || !clienteId}
                data-testid="plan-generar-btn"
              >
                {loadingPlan ? <Spinner /> : 'Generar plan'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumen header + warnings */}
      {plan && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                {plan.sistema} {plan.version} → {plan.target.cliente_nombre}
                {plan.target.empresa_nombre && ` / ${plan.target.empresa_nombre}`}
                {plan.target.bd && ` (${plan.target.bd})`}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={refrescarManifest}
                disabled={refreshing}
                data-testid="plan-refresh-manifest-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                Refrescar manifest
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="plan-totales">
              <Badge variant="outline">{plan.totales.manifest} en manifest</Badge>
              <Badge variant="default">{plan.totales.aplicables} aplicables</Badge>
              <Badge variant="secondary">{plan.totales.aplicados} aplicados</Badge>
              <Badge variant="destructive">{plan.totales.pendientes} pendientes</Badge>
              {plan.totales.skip_por_bd > 0 && (
                <Badge variant="outline">{plan.totales.skip_por_bd} skip sin BD</Badge>
              )}
              {plan.totales.skip_por_licencia > 0 && (
                <Badge variant="outline">{plan.totales.skip_por_licencia} skip licencia</Badge>
              )}
            </div>
            {isAged && (
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200 p-3 rounded mb-2 text-sm">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Manifest snapshot tiene {plan.snapshot_age_days}d. Considerá click "Refrescar manifest" para traer los cambios recientes del repo.
              </div>
            )}
            {!plan.features_verificadas && (
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-200 p-3 rounded text-sm">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Sin verificación de licencias (tabla empresa_features no disponible). Validá manualmente que la empresa tiene las features necesarias para aplicar artifacts con <code className="px-1 bg-blue-100 dark:bg-blue-900 rounded">required_for_empresa: feature_QFNNN</code>.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabla agrupada por phase */}
      {plan && plan.fases.length === 0 && (
        <EmptyState
          icon={FileCode2}
          title="Sin artifacts"
          description="El manifest actual del sistema no declara artifacts."
        />
      )}

      {plan && plan.fases.map((fase) => (
        <Card key={fase.phase}>
          <CardHeader>
            <CardTitle className="text-base">
              Fase {fase.phase} <Badge variant="outline" className="ml-2">{fase.artifacts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">Artifact</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Scope</th>
                  <th className="p-2 text-left">Aplica</th>
                  <th className="p-2 text-left">Estado</th>
                  <th className="p-2 text-left">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {fase.artifacts.map((a) => {
                  const estado = ESTADO_BADGE[a.estado_actual] ?? ESTADO_BADGE.unknown;
                  return (
                    <tr
                      key={a.artifact_id}
                      className={`border-b ${!a.aplica ? 'opacity-60' : ''}`}
                      data-testid={`plan-row-${a.artifact_id}`}
                    >
                      <td className="p-2 font-mono text-xs">{a.artifact_id}</td>
                      <td className="p-2"><Badge variant="outline">{a.type}</Badge></td>
                      <td className="p-2 text-xs">{a.scope}</td>
                      <td className="p-2">
                        {a.aplica ? (
                          <Badge variant="default">Sí</Badge>
                        ) : (
                          <Badge variant="outline" title={a.skip_reason ?? ''}>
                            No {a.skip_reason ? `(${SKIP_REASON_LABEL[a.skip_reason] ?? a.skip_reason})` : ''}
                          </Badge>
                        )}
                      </td>
                      <td className="p-2">
                        <Badge variant={estado.variant}>{estado.label}</Badge>
                      </td>
                      <td className="p-2 flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewArtifact(a)}
                          data-testid={`plan-view-${a.artifact_id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" /> Ver
                        </Button>
                        {a.aplica && a.estado_actual !== 'match' && a.checksum_expected && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => setApplyArtifact(a)}
                            data-testid={`plan-apply-${a.artifact_id}`}
                            title="Generar apply_token + ejecutar via agente"
                          >
                            <Rocket className="h-4 w-4 mr-1" /> Aplicar
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      {!plan && !loadingPlan && (
        <EmptyState
          icon={ClipboardList}
          title="Generá un plan"
          description="Seleccioná Sistema + Cliente arriba y click 'Generar plan' para ver qué scripts faltan aplicar."
        />
      )}

      {/* Modal viewer del script */}
      {viewArtifact && plan && (
        <ViewScriptModal
          sistema={plan.sistema}
          artifact={viewArtifact}
          onClose={() => setViewArtifact(null)}
        />
      )}

      {/* Q-930 Sprint 7: Dialog apply artifact (emite token + curl helper) */}
      {applyArtifact && plan && (
        <ApplyArtifactDialog
          open={true}
          onClose={() => setApplyArtifact(null)}
          plan={plan}
          artifact={applyArtifact}
          onApplied={generarPlan}
        />
      )}
    </div>
  );
}
